import React, { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { QK } from "../../../../../../base/qk";
import { useSnackbar } from "notistack";
import {
  getAssessmentByClientID,
  createAssessment,
  deleteAssessmentID,
} from "../../../../../../api/services/observations";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CustomInput from "../../../../../CustomForm/CustomInput";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomDateTime from "../../../../../CustomForm/CustomDateTime";
import { useUserProfile } from "../../../../../../hooks/userProfile";
import DataTable from "../../../../../Table/DataTable";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import EditPulseModal from "./editPulseModal";
import dayjs from "dayjs";

const Pulse = () => {
  const [formData, setFormData] = useState({
    date_time: dayjs().format("YYYY-MM-DD HH:mm"),
  });
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [RowID, setRowID] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [selectedID, setSelectedID] = useState(null);
  const assessmentType = "pulse";

  const profile = useUserProfile();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedFormData = {
        ...formData,
        client: clientID,
        carer: profile.id,
        assessment_type: assessmentType,
      };
      const response = await createAssessment(updatedFormData);
      if (response.status === 201) {
        enqueueSnackbar("Data has been created Successfully.", {
          variant: "success",
        });
        setFormData({
          date_time: dayjs().format("YYYY-MM-DD HH:mm"),
        });
        queryClient.invalidateQueries(QK.pulse_rates);
      }
    } catch (error) {
      enqueueSnackbar(
        "An error occurred while creating the Pulse Data.",
        { variant: "error" }
      );
    }
  };

  const handleCheckBox = async (isChecked, name) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: isChecked,
    }));
  };

  const handleOpenModal = (id) => {
    setSelectedID(id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedID(null);
  };

  const { data: assessments, isLoading } = useQuery([QK.pulse_rates, clientID], () =>
    getAssessmentByClientID(clientID, assessmentType)
  );

  const deleteMutation = useMutation(deleteAssessmentID, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.pulse_rates]);
      enqueueSnackbar("Data deleted successfully", { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar("Failed to delete Data", { variant: "error" });
    },
  });

  const handleUpdate = (id) => {
    handleOpenModal(id);
  };

  const headers = [
    { key: "value", label: "Pulse Rate" },
    { key: "notes", label: "Notes" },
    { key: "date_time", label: "Date & Time" },
  ];

  const renderCell = (item, header) => {
    const key = typeof header === "object" ? header.key : header;

    if (key === "value") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FavoriteIcon sx={{ color: "#EF4444", fontSize: 16 }} />
          <Typography fontWeight={600}>{item.value || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">
            bpm
          </Typography>
        </Box>
      );
    }

    if (key === "date_time") {
      if (!item.date_time) return "-";
      const date = new Date(item.date_time);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    return item[key] || "-";
  };

  const handleDelete = (ID) => {
    setRowID(ID);
    setShowConfirmModal(true);
  };

  const handleClose = () => {
    setShowConfirmModal(false);
  };

  const handleConfirm = () => {
    deleteMutation.mutate(RowID);
    handleClose();
  };

  return (
    <Box>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
        }}
      >
        <CardHeader
          sx={{
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
            py: 1.5,
            px: 2.5,
          }}
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  bgcolor: "#FEE2E2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FavoriteIcon sx={{ color: "#EF4444", fontSize: 18 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} color="#1A1A2E">
                Pulse Rate
              </Typography>
            </Box>
          }
        />

        <CardContent sx={{ p: 2.5 }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "1 1 200px", minWidth: "180px" }}>
                <CustomInput
                  label="Pulse Rate*"
                  name="value"
                  type="number"
                  onChange={handleChange}
                  value={formData?.value || ""}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter pulse rate"
                  InputProps={{
                    endAdornment: (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mr: 1 }}
                      >
                        bpm
                      </Typography>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ flex: "1 1 200px", minWidth: "180px" }}>
                <CustomDateTime
                  name="date_time"
                  label="Date / Time"
                  onChange={handleChange}
                  value={formData?.date_time || ""}
                  required
                  format="YYYY-MM-DD HH:mm"
                />
              </Box>

              <Box sx={{ flex: "1 1 200px", minWidth: "180px" }}>
                <CustomTextArea
                  label="Notes"
                  name="notes"
                  onChange={handleChange}
                  value={formData?.notes || ""}
                  fullWidth
                  size="small"
                  placeholder="Add any notes..."
                  rows={1}
                />
              </Box>

              <Box sx={{ width: "100%", mt: 1 }}>
                <Stack
                  direction="row"
                  spacing={3}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <CustomCheckbox
                    label="Share with Manager"
                    name="manager"
                    onClick={handleCheckBox}
                    checked={formData?.manager || false}
                  />
                  <CustomCheckbox
                    label="Handover"
                    name="handover"
                    onClick={handleCheckBox}
                    checked={formData?.handover || false}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      bgcolor: "#2469F0",
                      px: 3,
                      "&:hover": {
                        bgcolor: "#1A5BD4",
                        boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                      },
                    }}
                  >
                    Save Pulse Rate
                  </Button>
                </Stack>
              </Box>
            </Box>
          </form>

          <Divider sx={{ my: 2.5 }} />

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} color="#1A1A2E">
                Pulse Rate History
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {assessments?.length || 0} records
              </Typography>
            </Box>

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <DataTable
                data={assessments || []}
                headers={headers}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                canUpdate={true}
                canDelete={true}
                renderCell={renderCell}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        title="Confirm Delete"
        body="Are you sure you want to delete this data?"
      />

      <EditPulseModal
        show={showModal}
        handleClose={handleCloseModal}
        selectedID={selectedID}
      />
    </Box>
  );
};

export default Pulse;